import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, FileText, RotateCcw } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";

const STATUSES = ["monitoring", "investigating", "verified", "disputed", "unresolved", "closed"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const STATUS_COLOR = {
  monitoring: "text-sky-400", investigating: "text-amber-400", verified: "text-emerald-400",
  disputed: "text-red-400", unresolved: "text-fuchsia-400", closed: "text-zinc-500",
};
const PRIORITY_COLOR = { low: "text-zinc-400", medium: "text-sky-400", high: "text-amber-400", critical: "text-red-400" };

function Stat({ label, value, color, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`font-tactical font-black text-2xl ${color || "text-white"}`}>{value ?? 0}</div>
      <div className="label-caps text-zinc-500 mt-1">{label}</div>
    </Card>
  );
}

export default function GhanaIntel() {
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [records, setRecords] = useState([]);
  const [topics, setTopics] = useState([]);
  const [regions, setRegions] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [filters, setFilters] = useState({ status: "", priority: "", topic: "", region: "", institution: "", entity: "", date_from: "", date_to: "", q: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [refsReady, setRefsReady] = useState(false);
  const [tab, setTab] = useState("active"); // active | archived
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [loading, setLoading] = useState(false);

  function blank() {
    return { title: "", summary: "", topic: "", regions: [], institutions: [], entities: "", event_date: "", priority: "medium", reliability: "", analyst_notes: "" };
  }

  const loadRefs = () => {
    Promise.all([
      api.get("/ghana-intel/topics").then((r) => setTopics(r.data)),
      api.get("/ghana-intel/regions").then((r) => setRegions(r.data.regions)),
      api.get("/ghana-intel/institutions").then((r) => setInstitutions(r.data)),
    ]).then(() => setRefsReady(true)).catch(() => setRefsReady(true));
    api.get("/ghana-intel/dashboard").then((r) => setDash(r.data)).catch(() => {});
  };
  const loadRecords = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (tab === "archived") params.archived = true;
    setLoading(true);
    setRecords([]);
    api.get("/ghana-intel/records", { params })
      .then((r) => setRecords(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filters, tab]);

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  const doRestore = async () => {
    if (restoring || !restoreTarget) return;
    setRestoring(true);
    try {
      await api.post(`/ghana-intel/records/${restoreTarget.id}/restore`);
      toast.success("Record restored");
      setRestoreTarget(null);
      loadRecords();
      loadRefs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRestoring(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form, entities: form.entities.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.post("/ghana-intel/records", payload);
      toast.success("Intelligence record created");
      setOpen(false);
      setForm(blank());
      loadRefs();
      nav(`/command-center/ghana-intel/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  return (
    <div className="p-6">
      <PageHeader title="Ghana Intelligence" subtitle="Structured monitoring, research, verification and analysis — private workspace.">
        <Btn variant="ghost" data-testid="gi-briefs-btn" onClick={() => nav("/command-center/ghana-intel/briefs")}><FileText className="h-4 w-4" /> Briefs</Btn>
        <Btn data-testid="gi-new-record-btn" disabled={!refsReady} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> {refsReady ? "New Record" : "Loading…"}</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <Stat label="New / Monitoring" value={dash?.new_items} color="text-sky-400" testid="gi-stat-new" />
        <Stat label="Investigating" value={dash?.investigating} color="text-amber-400" testid="gi-stat-investigating" />
        <Stat label="Verified" value={dash?.verified} color="text-emerald-400" testid="gi-stat-verified" />
        <Stat label="Disputed" value={dash?.disputed} color="text-red-400" testid="gi-stat-disputed" />
        <Stat label="Unresolved" value={dash?.unresolved} color="text-fuchsia-400" testid="gi-stat-unresolved" />
        <Stat label="High Priority" value={dash?.high_priority} color="text-amber-300" testid="gi-stat-priority" />
        <Stat label="Published Ghana" value={dash?.published_ghana_analysis} testid="gi-stat-published" />
      </div>

      <Card className="p-4 mb-6">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Search"><input data-testid="gi-filter-q" className={inputCls} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Title, summary, notes" /></Field>
          <Field label="Status"><select data-testid="gi-filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Priority"><select data-testid="gi-filter-priority" className={inputCls} value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}><option value="">All</option>{PRIORITIES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Topic"><select data-testid="gi-filter-topic" className={inputCls} value={filters.topic} onChange={(e) => setFilters({ ...filters, topic: e.target.value })}><option value="">All</option>{topics.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}</select></Field>
          <Field label="Region"><select data-testid="gi-filter-region" className={inputCls} value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}><option value="">All</option>{regions.map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
          <Field label="Institution"><select data-testid="gi-filter-institution" className={inputCls} value={filters.institution} onChange={(e) => setFilters({ ...filters, institution: e.target.value })}><option value="">All</option>{institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></Field>
          <Field label="Entity"><input data-testid="gi-filter-entity" className={inputCls} value={filters.entity} onChange={(e) => setFilters({ ...filters, entity: e.target.value })} placeholder="Person / entity" /></Field>
          <Field label="From"><input type="date" data-testid="gi-filter-from" className={inputCls} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></Field>
          <Field label="To"><input type="date" data-testid="gi-filter-to" className={inputCls} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></Field>
        </div>
      </Card>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`gi-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t}
          </button>
        ))}
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="gi-records-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Topic</th>
              <th className="px-4 py-2.5">Regions</th><th className="px-4 py-2.5">Priority</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">{tab === "archived" ? "Archived" : "Updated"}</th>
              {tab === "archived" && <th className="px-4 py-2.5">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && <tr><td colSpan={tab === "archived" ? 7 : 6} className="px-4 py-6 text-zinc-600" data-testid="gi-loading">Loading…</td></tr>}
            {!loading && records.length === 0 && <tr><td colSpan={tab === "archived" ? 7 : 6} className="px-4 py-6 text-zinc-600" data-testid="gi-empty">{tab === "archived" ? "No archived records." : "No intelligence records match."}</td></tr>}
            {!loading && records.map((r) => (
              <tr key={r.id} className={`hover:bg-zinc-900/50 ${tab === "active" ? "cursor-pointer" : ""}`} data-testid={`gi-row-${r.id}`} onClick={() => tab === "active" && nav(`/command-center/ghana-intel/${r.id}`)}>
                <td className="px-4 py-2.5 font-medium">{r.title}</td>
                <td className="px-4 py-2.5 text-zinc-400">{r.topic || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-500 text-xs">{(r.regions || []).join(", ") || "—"}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${PRIORITY_COLOR[r.priority]}`}>{r.priority}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${STATUS_COLOR[r.status]}`}>{r.status}</td>
                <td className="px-4 py-2.5 text-zinc-600 text-xs">{(tab === "archived" ? r.deleted_at : r.updated_at) ? new Date(tab === "archived" ? r.deleted_at : r.updated_at).toLocaleDateString() : "—"}</td>
                {tab === "archived" && (
                  <td className="px-4 py-2.5">
                    <button data-testid={`gi-restore-${r.id}`} onClick={(e) => { e.stopPropagation(); setRestoreTarget(r); }}
                      className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!restoreTarget} onClose={() => { if (!restoring) setRestoreTarget(null); }} title="Restore intelligence record">
        {restoreTarget && (
          <div className="space-y-4" data-testid="gi-restore-confirm">
            <p className="text-sm text-zinc-400">Restore this archived record to the active workspace? All prior data (status, priority, notes and linked sources/claims/media) is preserved.</p>
            <div className="text-sm font-mono-data break-all" data-testid="gi-restore-confirm-title">{restoreTarget.title}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="gi-restore-cancel" disabled={restoring} onClick={() => setRestoreTarget(null)}>Cancel</Btn>
              <Btn data-testid="gi-restore-confirm-btn" disabled={restoring} onClick={doRestore} className="flex-1 justify-center">{restoring ? "Restoring…" : "Restore"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Intelligence Record" wide>
        <form onSubmit={create} className="space-y-4">
          <Field label="Title"><input data-testid="gi-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Summary"><textarea data-testid="gi-summary-input" className={inputCls} rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Topic"><select data-testid="gi-topic-input" className={inputCls} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}><option value="">—</option>{topics.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}</select></Field>
            <Field label="Priority"><select data-testid="gi-priority-input" className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event date"><input type="date" className={inputCls} value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></Field>
            <Field label="Reliability"><input className={inputCls} value={form.reliability} onChange={(e) => setForm({ ...form, reliability: e.target.value })} placeholder="e.g. A/B/C or High/Med/Low" /></Field>
          </div>
          <Field label="Regions">
            <div className="flex flex-wrap gap-1.5">
              {regions.map((r) => <button key={r} type="button" onClick={() => toggle("regions", r)} className={`text-xs px-2 py-1 rounded-sm border ${form.regions.includes(r) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>{r}</button>)}
            </div>
          </Field>
          <Field label="Institutions">
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
              {institutions.map((i) => <button key={i.id} type="button" onClick={() => toggle("institutions", i.id)} className={`text-xs px-2 py-1 rounded-sm border ${form.institutions.includes(i.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}>{i.name}</button>)}
            </div>
          </Field>
          <Field label="Person / entity references (comma separated)"><input data-testid="gi-entities-input" className={inputCls} value={form.entities} onChange={(e) => setForm({ ...form, entities: e.target.value })} /></Field>
          <Field label="Analyst notes (never public)"><textarea className={inputCls} rows={3} value={form.analyst_notes} onChange={(e) => setForm({ ...form, analyst_notes: e.target.value })} /></Field>
          <Btn data-testid="gi-save-btn" type="submit" disabled={saving} className="w-full justify-center">{saving ? "Creating…" : "Create Record"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
